/**
 * Inventing a whole piece, not just reshuffling one.
 *
 * Re-rolling the seed of an existing piece barely changes what you hear, and
 * the reason is worth stating because it is not obvious. Pad and bass carry no
 * randomness at all: they come straight from the key and the progression. So
 * two seeds share an identical harmonic bed, and only the figures on top move.
 *
 * Real variety has to come from the level above: a different key, a different
 * mode, a different tempo, different chords, different instruments. That is
 * what this file does, and it is only possible because a piece is constraints
 * rather than notes. There is nothing here that could be done to a list of note
 * names.
 */

import { INSTRUMENTS_BY_ROLE } from "./engine.js";
import {
  type FigureStyle, type LayerRole, type LayerSpec, type Piece, resolveTiming,
} from "./piece.js";
import { makeRng, type Rng } from "./rng.js";
import { type Mode, parseProgression } from "./theory.js";

const TONICS = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

/**
 * Moods, each a mode plus the progressions and tempos that suit it.
 *
 * Grouped rather than rolled independently because most combinations are bad.
 * A bright lydian progression at 54bpm under a sub bass is a valid piece and
 * nobody wants it, and a randomiser that mostly produces those trains people
 * to stop pressing the button.
 */
export interface Mood {
  name: string;
  /**
   * Which half of the list this belongs to, and it is required rather than
   * derived on purpose.
   *
   * Seventeen chips in one wall is a wall. Split in two it reads as a choice
   * between two kinds of music. Guessing the family from whether a style has
   * drums almost works and then quietly files something in the wrong place,
   * which is the failure this project is otherwise against.
   */
  family: "calm" | "moving";
  mode: Mode;
  tempo: [number, number];
  progressions: string[];
  /**
   * Bars each chord is held for. This is harmonic rhythm, and it changes the
   * character of a piece more than tempo does: the same chords at the same
   * speed are ambient at four bars each and a song at one.
   */
  barsPerChord?: number[];
  /** Grooves that suit. Omit for no percussion at all. */
  grooves?: string[];
  /** Instruments to prefer, overriding the by-role defaults. */
  palette?: Partial<Record<LayerRole, string[]>>;
  /** Where rhythm and melody sit on the intensity slider. */
  enters?: { rhythm: [number, number]; melody: [number, number]; perc: [number, number] };
  /** How the melodic layers lay their notes out. Phrases unless stated. */
  figure?: Partial<Record<"rhythm" | "melody", FigureStyle[]>>;
  /** How busy the melodic layers are, before the roll jitters it. */
  density?: [number, number];
  /**
   * How much room, 0 to 1.
   *
   * The ambient styles want a lot of it and the fast ones want very little: a
   * long tail under a 150bpm beat is mud. This is the single biggest difference
   * between a style sounding finished and sounding like an oscillator demo.
   */
  space?: [number, number];
}

const MOODS: Mood[] = [
  // The quiet half. These used to differ only in mode and chords, which under a
  // four bar pad is a colour change and nothing else, so all six sounded like
  // one piece. They now differ in harmonic rhythm, palette, density, figure
  // style and whether anything keeps time, which is what actually separates
  // them to a listener.

  {
    name: "settled",
    family: "calm",
    space: [0.42, 0.58],
    mode: "minor",
    tempo: [64, 88],
    progressions: [
      "i - VI - III - VII",
      "i - VII - VI - VII",
      "i - iv - VI - V",
      "i - III - VII - VI",
      "i - VI - iv - V",
    ],
    barsPerChord: [4],
    palette: {
      pad: ["glass_pad", "warm_pad"],
      bass: ["deep_bass", "bass"],
      rhythm: ["bell", "celeste"],
      melody: ["celeste", "glass_bell"],
    },
    density: [0.2, 0.4],
  },
  {
    name: "hopeful",
    family: "calm",
    space: [0.34, 0.48],
    mode: "major",
    tempo: [76, 104],
    progressions: [
      "I - V - vi - IV",
      "I - IV - vi - V",
      "vi - IV - I - V",
      "I - iii - IV - I",
      "I - vi - ii - V",
    ],
    barsPerChord: [2],
    // A harp running gently under a major progression is a completely
    // different room from a bell landing every few bars.
    grooves: ["brush"],
    palette: {
      pad: ["air_pad", "string_pad"],
      bass: ["warm_bass", "bass"],
      rhythm: ["harp", "celeste"],
      melody: ["flute", "chime"],
    },
    figure: { rhythm: ["arpeggio"], melody: ["motif"] },
    density: [0.4, 0.6],
    enters: { perc: [0.25, 0.4], rhythm: [0.15, 0.3], melody: [0.5, 0.7] },
  },
  {
    name: "drifting",
    family: "calm",
    space: [0.55, 0.75],
    mode: "dorian",
    tempo: [58, 80],
    progressions: [
      "i - IV - i - VII",
      "i - IV - VII - i",
      "i - ii - IV - i",
      "i - VII - IV - i",
    ],
    barsPerChord: [4, 8],
    palette: {
      pad: ["air_pad", "glass_pad"],
      bass: ["deep_bass"],
      rhythm: ["glass_bell", "chime"],
      melody: ["flute", "harp"],
    },
    // Almost nothing happening, over chords that barely move. The sparsest
    // thing here and the only one that holds a chord for eight bars.
    density: [0.1, 0.25],
  },
  {
    name: "uneasy",
    family: "calm",
    space: [0.4, 0.58],
    mode: "phrygian",
    tempo: [54, 76],
    progressions: [
      "i - II - i - VII",
      "i - II - VII - i",
      "i - vii - II - i",
    ],
    barsPerChord: [2, 4],
    // A rim once a bar under a phrygian pad is unsettling in a way silence is
    // not, because something is counting and you cannot ignore it.
    grooves: ["tick", "breath"],
    palette: {
      pad: ["string_pad", "organ_pad"],
      bass: ["deep_bass", "warm_bass"],
      rhythm: ["rim", "pluck"],
      melody: ["glass_bell", "chime"],
    },
    figure: { rhythm: ["pulse"], melody: ["motif"] },
    density: [0.15, 0.35],
    enters: { perc: [0.15, 0.3], rhythm: [0.3, 0.45], melody: [0.55, 0.75] },
  },
  {
    name: "wondering",
    family: "calm",
    space: [0.38, 0.55],
    mode: "lydian",
    tempo: [70, 96],
    progressions: [
      "I - II - I - vii",
      "I - II - vi - I",
      "I - iii - II - I",
    ],
    barsPerChord: [2, 4],
    palette: {
      pad: ["glass_pad", "air_pad"],
      bass: ["bass", "warm_bass"],
      rhythm: ["celeste", "glass_bell", "harp"],
      melody: ["chime", "celeste"],
    },
    // Lydian's raised fourth is the whole character, and it is heard clearest
    // in a run through the chord rather than a phrase that might skip it.
    figure: { rhythm: ["arpeggio"], melody: ["arpeggio", "motif"] },
    density: [0.45, 0.7],
  },
  {
    name: "warm",
    family: "calm",
    space: [0.3, 0.45],
    mode: "mixolydian",
    tempo: [72, 100],
    progressions: [
      "I - VII - IV - I",
      "I - v - IV - I",
      "I - IV - VII - IV",
    ],
    barsPerChord: [2],
    grooves: ["breath", "brush"],
    palette: {
      pad: ["warm_pad", "organ_pad"],
      bass: ["warm_bass", "pluck_bass"],
      rhythm: ["harp", "pluck"],
      melody: ["flute", "celeste"],
    },
    density: [0.35, 0.55],
    enters: { perc: [0.2, 0.35], rhythm: [0.25, 0.4], melody: [0.5, 0.7] },
  },

  // Everything above is ambient: slow harmony, no drums, layers that drift in.
  // Everything below is upbeat, and the difference is almost entirely the two
  // fields the ambient moods leave out. Adding a genre is a block of data here.

  {
    name: "bright",
    family: "moving",
    space: [0.12, 0.24],
    mode: "major",
    tempo: [112, 134],
    progressions: [
      "I - V - vi - IV",
      "I - IV - V - IV",
      "vi - IV - I - V",
      "I - V - IV - V",
    ],
    barsPerChord: [1, 1, 2],
    grooves: ["fourFloor", "backbeat", "driving", "drivingOpen", "pushed", "offbeat"],
    palette: {
      pad: ["organ_pad", "string_pad", "warm_pad"],
      bass: ["pluck_bass", "warm_bass", "bass"],
      rhythm: ["pluck", "celeste", "glass_bell"],
      melody: ["flute", "celeste", "chime"],
    },
    // Drums first, then the hook. In an upbeat piece the beat is the floor
    // rather than a warning that something is wrong.
    enters: { perc: [0.0, 0.15], rhythm: [0.2, 0.35], melody: [0.45, 0.65] },
  },

  {
    name: "driving",
    family: "moving",
    space: [0.1, 0.2],
    mode: "minor",
    tempo: [124, 146],
    progressions: [
      "i - VII - VI - VII",
      "i - VI - III - VII",
      "i - v - VI - iv",
      "i - iv - VII - III",
    ],
    barsPerChord: [1, 1, 2],
    grooves: ["driving", "drivingOpen", "drivingBusy", "pushed", "fourFloor", "marching", "scatter"],
    palette: {
      pad: ["string_pad", "organ_pad"],
      bass: ["pluck_bass", "bass", "deep_bass"],
      rhythm: ["pluck", "rim", "glass_bell"],
      melody: ["chime", "flute", "celeste"],
    },
    figure: { rhythm: ["motif", "arpeggio"], melody: ["motif"] },
    enters: { perc: [0.0, 0.1], rhythm: [0.15, 0.3], melody: [0.4, 0.6] },
  },

  {
    name: "loping",
    family: "moving",
    space: [0.18, 0.3],
    mode: "dorian",
    tempo: [88, 108],
    progressions: [
      "i - IV - i - IV",
      "i - IV - VII - IV",
      "i - ii - IV - VII",
    ],
    barsPerChord: [1, 2, 2],
    // Triplet feel, which is the whole identity of this one and lives entirely
    // in the groove's stepsPerBar.
    grooves: ["skip", "halfTime", "pulse"],
    palette: {
      pad: ["warm_pad", "organ_pad", "air_pad"],
      bass: ["warm_bass", "pluck_bass"],
      rhythm: ["harp", "pluck", "celeste"],
      melody: ["flute", "harp", "glass_bell"],
    },
    enters: { perc: [0.05, 0.2], rhythm: [0.25, 0.4], melody: [0.5, 0.7] },
  },

  {
    name: "urgent",
    family: "moving",
    space: [0.08, 0.16],
    mode: "phrygian",
    tempo: [132, 158],
    progressions: [
      "i - II - i - VII",
      "i - VII - II - i",
      "i - II - VI - VII",
    ],
    barsPerChord: [1, 1, 2],
    grooves: ["driving", "marching", "fourFloor"],
    palette: {
      pad: ["string_pad", "organ_pad"],
      bass: ["deep_bass", "bass", "pluck_bass"],
      rhythm: ["rim", "pluck"],
      melody: ["chime", "flute"],
    },
    enters: { perc: [0.0, 0.1], rhythm: [0.1, 0.25], melody: [0.35, 0.55] },
  },

  {
    name: "mechanical",
    family: "moving",
    space: [0.1, 0.2],
    mode: "dorian",
    tempo: [104, 126],
    // Harmony that barely moves, on purpose. A machine does not modulate, and
    // the interest has to come from the parts rather than the chords.
    progressions: [
      "i - i - VII - i",
      "i - VII - i - VII",
      "i - i - IV - IV",
    ],
    barsPerChord: [1],
    grooves: ["gallop", "driving", "scatter"],
    palette: {
      pad: ["organ_pad", "string_pad"],
      bass: ["pluck_bass", "bass"],
      rhythm: ["pluck", "rim"],
      melody: ["glass_bell", "chime"],
    },
    // Both parts running, neither varying. This is the one that sounds least
    // like everything else here, and it is entirely the figure style.
    figure: { rhythm: ["arpeggio"], melody: ["arpeggio", "pulse"] },
    density: [0.7, 0.95],
    enters: { perc: [0.0, 0.1], rhythm: [0.0, 0.15], melody: [0.4, 0.6] },
  },

  {
    name: "playful",
    family: "moving",
    space: [0.16, 0.28],
    mode: "lydian",
    tempo: [118, 142],
    progressions: [
      "I - II - vi - I",
      "I - V - II - I",
      "I - vi - II - V",
    ],
    barsPerChord: [1, 2],
    grooves: ["offbeat", "skip", "brush"],
    palette: {
      pad: ["glass_pad", "air_pad"],
      bass: ["pluck_bass"],
      rhythm: ["celeste", "glass_bell", "pluck"],
      melody: ["chime", "celeste", "harp"],
    },
    figure: { rhythm: ["arpeggio"], melody: ["motif"] },
    density: [0.6, 0.9],
    enters: { perc: [0.05, 0.2], rhythm: [0.15, 0.3], melody: [0.4, 0.6] },
  },

  {
    name: "triumphant",
    family: "moving",
    space: [0.24, 0.38],
    mode: "mixolydian",
    tempo: [96, 118],
    progressions: [
      "I - IV - VII - I",
      "I - V - IV - I",
      "IV - I - V - I",
    ],
    barsPerChord: [2],
    grooves: ["marching", "fourFloor", "stomp"],
    palette: {
      pad: ["organ_pad", "string_pad"],
      bass: ["deep_bass", "warm_bass"],
      rhythm: ["bell", "chime"],
      melody: ["flute", "chime"],
    },
    density: [0.35, 0.55],
    enters: { perc: [0.1, 0.25], rhythm: [0.25, 0.4], melody: [0.4, 0.6] },
  },

  {
    name: "heavy",
    family: "moving",
    space: [0.26, 0.4],
    mode: "minor",
    tempo: [62, 80],
    progressions: [
      "i - VI - i - VII",
      "i - iv - i - VI",
      "i - VI - VII - i",
    ],
    // Slow AND with drums, which is a different thing from ambient. Half time
    // over a long chord is weight rather than calm.
    barsPerChord: [2, 4],
    grooves: ["stomp", "halfTime"],
    palette: {
      pad: ["string_pad", "organ_pad"],
      bass: ["deep_bass"],
      rhythm: ["rim", "pluck"],
      melody: ["chime", "glass_bell"],
    },
    figure: { rhythm: ["pulse", "motif"], melody: ["motif"] },
    density: [0.2, 0.4],
    enters: { perc: [0.0, 0.15], rhythm: [0.3, 0.45], melody: [0.55, 0.75] },
  },

  {
    name: "pursuit",
    family: "moving",
    space: [0.06, 0.14],
    mode: "phrygian",
    tempo: [142, 168],
    progressions: [
      "i - i - II - i",
      "i - VII - i - II",
      "i - II - VII - VI",
    ],
    barsPerChord: [1],
    grooves: ["gallop", "scatter", "driving"],
    palette: {
      pad: ["string_pad"],
      bass: ["deep_bass", "pluck_bass"],
      rhythm: ["rim", "pluck"],
      melody: ["chime"],
    },
    figure: { rhythm: ["arpeggio"], melody: ["pulse", "motif"] },
    density: [0.75, 1.0],
    enters: { perc: [0.0, 0.05], rhythm: [0.05, 0.2], melody: [0.3, 0.5] },
  },

  {
    // Something very large moving slowly, with detail on the surface.
    //
    // The only style that pulls harmonic rhythm and figure density in opposite
    // directions: eight bars a chord, which is the slowest harmony here, under
    // the busiest arpeggio. Everything else moves its chords and its surface at
    // roughly the same rate, so this is the one contrast the format had left.
    //
    // Mixolydian because the flat seventh never quite lands, and two chords
    // because a tide is not a chord progression, it is one thing going out and
    // coming back.
    name: "tidal",
    family: "calm",
    space: [0.5, 0.7],
    mode: "mixolydian",
    tempo: [58, 74],
    // Two chords only. Four of them at eight bars each is a four minute cycle,
    // which is a long wait for a render and longer than the idea needs: a tide
    // is one thing going out and coming back.
    progressions: [
      "I - VII",
      "I - IV",
      "IV - I",
      "I - v",
    ],
    barsPerChord: [8],
    grooves: ["breath", "brush"],
    palette: {
      pad: ["air_pad", "string_pad"],
      bass: ["deep_bass", "warm_bass"],
      rhythm: ["harp", "celeste", "glass_bell"],
      melody: ["flute", "chime"],
    },
    figure: { rhythm: ["arpeggio"], melody: ["motif"] },
    density: [0.7, 0.95],
    // The surface is the point, so it is almost always there. The melody is
    // the thing that arrives.
    enters: { perc: [0.25, 0.4], rhythm: [0.05, 0.15], melody: [0.5, 0.7] },
  },

  {
    name: "nocturne",
    family: "calm",
    space: [0.4, 0.58],
    mode: "minor",
    tempo: [66, 84],
    progressions: [
      "i - iv - VII - III",
      "i - VI - ii - v",
      "i - v - VI - iv",
    ],
    barsPerChord: [2],
    grooves: ["brush", "tick"],
    palette: {
      pad: ["air_pad", "warm_pad"],
      bass: ["warm_bass", "bass"],
      rhythm: ["harp"],
      melody: ["flute", "harp", "celeste"],
    },
    // A harp arpeggio under a slow minor progression, which is the oldest
    // trick here and still the prettiest.
    figure: { rhythm: ["arpeggio"], melody: ["motif"] },
    density: [0.5, 0.75],
    enters: { perc: [0.3, 0.45], rhythm: [0.1, 0.25], melody: [0.45, 0.65] },
  },
];

export const MOOD_NAMES = MOODS.map((m) => m.name);

/** Every progression any style can produce, for the editor to offer. */
export const ALL_PROGRESSIONS = [...new Set(MOODS.flatMap((m) => m.progressions))].sort();

/** Names split the way the editor shows them. */
export const MOOD_FAMILIES: { calm: string[]; moving: string[] } = {
  calm: MOODS.filter((m) => m.family === "calm").map((m) => m.name),
  moving: MOODS.filter((m) => m.family === "moving").map((m) => m.name),
};

export interface MoodSummary {
  name: string;
  mode: Mode;
  tempo: [number, number];
  barsPerChord: number[];
  grooves: string[];
  chords: string[];
}

/**
 * What each style actually is, for the reference the editor shows.
 *
 * Derived from `MOODS` rather than written out again, so a style added here
 * documents itself and a glossary cannot drift from the generator.
 */
export const MOOD_SUMMARIES: MoodSummary[] = MOODS.map((m) => ({
  name: m.name,
  mode: m.mode,
  tempo: m.tempo,
  barsPerChord: m.barsPerChord ?? [2, 4],
  grooves: m.grooves ?? [],
  chords: m.progressions,
}));

export interface RandomOptions {
  /**
   * Force a mood: a built-in name, or a whole mood of someone's own.
   *
   * Taking an object rather than only a name is what lets a person invent a
   * feeling without this file knowing about it.
   */
  mood?: string | Mood;
  /** Keep the piece loopable and roughly this long, in seconds. */
  duration?: number;
}

/**
 * Turn one piece into a feeling that can be rolled again.
 *
 * A piece fixes every choice; a mood leaves them open within a range. So the
 * settings become a band around themselves: the tempo becomes a spread, the
 * densities and thresholds get a little room, and the things that are not
 * numbers (mode, chords, instruments, groove) are kept exactly.
 *
 * That widening is the whole difference between saving a track and inventing a
 * style. Without it, rolling your own feeling would return the same piece.
 */
export function moodFromPiece(piece: Piece, name: string): Mood {
  const [, modeName = "major"] = piece.key.trim().split(/\s+/);
  const spread = (value: number, by: number, min: number, max: number): [number, number] => [
    Math.max(min, round(value - by, 2)),
    Math.min(max, round(value + by, 2)),
  ];

  const rhythm = piece.layers.rhythm;
  const melody = piece.layers.melody;
  const perc = piece.layers.perc;
  const density = rhythm?.density ?? melody?.density ?? 0.5;

  const palette: Mood["palette"] = {};
  for (const role of ["pad", "bass", "rhythm", "melody"] as const) {
    const instrument = piece.layers[role]?.instrument;
    if (instrument) palette[role] = [instrument];
  }

  return {
    name,
    // Filed by whether it has a beat, which is the distinction the two lists
    // are actually making to someone looking at them.
    family: perc?.groove && perc.groove !== "none" ? "moving" : "calm",
    mode: modeName.toLowerCase() as Mode,
    tempo: [
      Math.max(30, Math.round(piece.tempo * 0.92)),
      Math.min(200, Math.round(piece.tempo * 1.08)),
    ],
    progressions: [piece.progression],
    barsPerChord: [piece.barsPerChord ?? 4],
    space: spread(piece.space ?? 0.28, 0.06, 0, 0.85),
    grooves: perc?.groove ? [perc.groove] : [],
    palette,
    figure: {
      rhythm: [rhythm?.figure ?? "motif"],
      melody: [melody?.figure ?? "motif"],
    },
    density: spread(density, 0.12, 0.05, 1),
    enters: {
      perc: spread(perc?.enterAt ?? 0.35, 0.06, 0, 1),
      rhythm: spread(rhythm?.enterAt ?? 0.4, 0.06, 0, 1),
      melody: spread(melody?.enterAt ?? 0.65, 0.06, 0, 1),
    },
  };
}

/** Round to a sensible number of decimal places, so the editor shows tidy values. */
const round = (value: number, places: number): number => {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
};

/**
 * Steps the editor's sliders move in.
 *
 * A rolled value has to land on one of these. A range input snaps whatever it
 * is given to its own step, so a volume of 0.054 became 0.055 the instant the
 * piece was shown, and every later render used the snapped value. The piece you
 * heard was quietly not the piece that was rolled, and nothing said so.
 *
 * Kept beside the generator rather than in the editor: the format should
 * produce values its own controls can represent, not lean on the controls to
 * tidy up afterwards.
 */
export const STEPS = {
  volume: 0.005,
  density: 0.05,
  variation: 0.05,
  enterAt: 0.05,
  space: 0.01,
  duration: 4,
} as const;

/** Snap to a step, keeping the result free of floating point dust. */
const quantise = (value: number, step: number): number =>
  Math.round(Math.round(value / step) * step * 1000) / 1000;

function layer(rng: Rng, role: LayerRole, enterAt: number, mood: Mood): LayerSpec {
  const spec: LayerSpec = {
    instrument: rng.pick(mood.palette?.[role] ?? INSTRUMENTS_BY_ROLE[role]),
    enterAt,
    volume: quantise(rng.range(role === "pad" ? 0.04 : 0.07, role === "pad" ? 0.09 : 0.14), STEPS.volume),
  };

  if (role === "pad") spec.octave = rng.pick([3, 3, 4]);
  if (role === "bass") spec.octave = rng.pick([1, 2, 2]);

  if (role === "rhythm" || role === "melody") {
    // Capped at 4 rather than 5. A melody builds its pool two octaves above
    // its own setting, so 5 reaches octave 7, and everything up there is
    // clamped to MAX_FREQ, which turns a rising line into a flat squeal.
    spec.octave = rng.pick([3, 4, 4, 4]);
    spec.density = quantise(rng.range(...(mood.density ?? [0.25, 0.75])), STEPS.density);
    spec.figure = rng.pick(mood.figure?.[role] ?? ["motif"]);
    // Two or three figures with a little movement. One is an ostinato and
    // four over four chords is no repetition at all, which is the wash this
    // generator used to produce.
    spec.motifs = rng.pick([1, 2, 2, 3]);
    spec.variation = quantise(rng.range(0.05, 0.3), STEPS.variation);
  }

  return spec;
}

export function randomPiece(seed: number, options: RandomOptions = {}): Piece {
  const rng = makeRng(seed);
  const mood =
    typeof options.mood === "object" ? options.mood
    : options.mood ? MOODS.find((m) => m.name === options.mood) ?? rng.pick(MOODS)
    : rng.pick(MOODS);

  const tonic = rng.pick(TONICS);
  const tempo = Math.round(rng.range(mood.tempo[0], mood.tempo[1]));

  // Harmonic rhythm. One bar a chord against four is the difference between a
  // song and a wash, and it matters more than any instrument choice.
  const barsPerChord = rng.pick(mood.barsPerChord ?? [2, 4, 4]);

  // Thresholds are jittered so a randomised piece does not always bring its
  // layers in at the same points on the slider. Upbeat styles override these,
  // because there the beat is the floor rather than a warning.
  const enters = mood.enters;
  const rhythmEnter = quantise(rng.range(...(enters?.rhythm ?? [0.3, 0.5])), STEPS.enterAt);
  const melodyEnter = enters
    ? quantise(rng.range(...enters.melody), STEPS.enterAt)
    : quantise(rng.range(rhythmEnter + 0.15, 0.85), STEPS.enterAt);

  const progression = rng.pick(mood.progressions);

  const layers: Piece["layers"] = {
    pad: layer(rng, "pad", 0, mood),
    bass: layer(rng, "bass", 0, mood),
    rhythm: layer(rng, "rhythm", rhythmEnter, mood),
    melody: layer(rng, "melody", melodyEnter, mood),
  };

  // Percussion only where the style calls for it. An ambient piece with a
  // backbeat is not a calmer version of a song, it is a different thing that
  // nobody asked for.
  if (mood.grooves?.length) {
    layers.perc = {
      ...layer(rng, "perc", quantise(rng.range(...(enters?.perc ?? [0.3, 0.45])), STEPS.enterAt), mood),
      groove: rng.pick(mood.grooves),
    };
  }

  const piece: Piece = {
    name: `${mood.name}_${tonic.toLowerCase().replace("#", "s")}_${seed}`,
    key: `${tonic} ${mood.mode}`,
    tempo,
    progression,
    duration: options.duration ?? 72,
    barsPerChord,
    seed,
    loop: true,
    space: quantise(rng.range(...(mood.space ?? [0.24, 0.36])), STEPS.space),
    layers,
  };

  // A slow tempo with four bars a chord makes one cycle longer than half of a
  // fixed 72 seconds, so the length snaps to a single cycle and the melody,
  // which enters on the second pass, never sounds at all. The roll chooses the
  // tempo, so the roll has to choose a length that fits it.
  //
  // Found by a test over every seed rather than by listening, which is the only
  // way this would have been found: it is a silent layer in a piece that still
  // sounds finished.
  //
  // Quantised for the same reason as everything else: the length slider moves
  // in fours, so an unrounded 116.36 would be snapped to 116 the moment the
  // editor showed it. Rounding here costs at most two seconds and never drops
  // below two cycles, because a cycle is always longer than four seconds.
  const { cycle } = resolveTiming(piece, parseProgression(progression).length);
  piece.duration = quantise(Math.max(piece.duration, cycle * 2), STEPS.duration);

  return piece;
}
